import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { toast } from '@grandvisionhq/common';
import { useFeatureToggles } from '@grandvisionhq/state';
import WishlistLoader from '../../../components/WishlistLoader.js';
import { pushWishlistEvent } from '../../../data-layer/wishlist.js';
import { useAddToShoppingList } from '../../../hooks/use-add-to-shopping-list.js';
import { useDeleteShoppingList } from '../../../hooks/use-delete-shopping-list.js';
import { useRemoveFromShoppinglist } from '../../../hooks/use-remove-from-shopping-list.js';
import { useWishlistAuthState } from '../../../hooks/use-wishlist-auth-state.js';
import { useWishlistStorage } from '../../../hooks/use-wishlist-storage.js';
import AnonymousWishlistButton from './AnonymousWishlistButton.js';
import UserWishlistButton from './UserWishlistButton.js';

const WishlistButtonContainer = ({ sku  })=>{
    const [localShoppingList, setLocalShoppingList, removeLocalShoppingList] = useWishlistStorage();
    const { isAuthLoading , isUserLoggedIn  } = useWishlistAuthState();
    const { deleteShoppingList  } = useDeleteShoppingList({
        removeLocalShoppingList,
        localShoppingListId: localShoppingList === null || localShoppingList === void 0 ? void 0 : localShoppingList.id
    });
    const handlePushWishlistEvent = (messageType)=>{
        pushWishlistEvent({
            siteFeature: 'product_detail_page',
            interactionType: 'wishlist_pdp_add_click',
            messageType,
            item: {
                sku
            }
        });
    };
    const { addToShoppinglist  } = useAddToShoppingList({
        setLocalShoppingList,
        localShoppingListId: localShoppingList === null || localShoppingList === void 0 ? void 0 : localShoppingList.id,
        isLoggedIn: isUserLoggedIn,
        onCompleted: (data)=>{
            // If the mutation "succeeds", the BE can technically still send back a failure in the response
            // and a 200. This is why we need to check if the shoppinglist is actually there.
            // In case of a failure the shoppinglist will be null.
            if (data.addToShoppinglist.__typename === 'AddToShoppinglistSuccessResponse') {
                toast.success('account.wishlist.add.success');
                handlePushWishlistEvent('item_added');
            }
            /*
          If the wishlist is empty after removing an item (i.e. mutating the list),
          we need to completely delete the list to prevent modifying an empty
          list with the same id. This in turn prevents CommerceTools from
          filling up with empty wish lists (there is a storage limit).
        */ if (data.addToShoppinglist.__typename === 'AddToShoppinglistSuccessResponse' && data.addToShoppinglist.result.items.length === 0) {
                deleteShoppingList({
                    shoppinglistId: data.addToShoppinglist.result.id
                });
            }
        },
        onError: ()=>{
            toast.failed('account.wishlist.add.error');
            handlePushWishlistEvent('error');
        }
    });
    const { removeFromShoppinglist , loading: isUpdateLoading  } = useRemoveFromShoppinglist({
        onCompleted: (data)=>{
            // If the mutation "succeeds", the BE can technically still send back a failure in the response
            // and a 200. This is why we need to check if the shoppinglist is actually there.
            // In case of a failure the shoppinglist will be null.
            if (data.removeFromShoppinglist.__typename == 'RemoveFromShoppinglistSuccessResponse') {
                toast.success('account.wishlist.remove.success');
                handlePushWishlistEvent('item_removed');
            }
            /*
          If the wishlist is empty after removing an item (i.e. mutating the list),
          we need to completely delete the list to prevent modifying an empty
          list with the same id. This in turn prevents CommerceTools from
          filling up with empty wish lists (there is a storage limit).
        */ if (data.removeFromShoppinglist.__typename === 'RemoveFromShoppinglistSuccessResponse' && data.removeFromShoppinglist.result.items.length === 0) {
                deleteShoppingList({
                    shoppinglistId: data.removeFromShoppinglist.result.id
                });
            }
        },
        onError: ()=>{
            toast.failed('account.wishlist.remove.error');
            handlePushWishlistEvent('error');
        }
    });
    const onWishlistClick = (action, shoppinglistId, event)=>{
        // On the PLP the wishlist button is wrapped in a link, so we need to prevent the default
        // behavior of the link to prevent navigation when clicking the button.
        event === null || event === void 0 ? void 0 : event.preventDefault();
        if (action === 'add') {
            addToShoppinglist([
                sku
            ], shoppinglistId);
        } else {
            removeFromShoppinglist([
                sku
            ], shoppinglistId);
        }
    };
    if (isAuthLoading) {
        return /*#__PURE__*/ React.createElement(WishlistLoader, {
            className: "wishlist-loader__skeleton--button"
        });
    }
    return !isAuthLoading && isUserLoggedIn ? /*#__PURE__*/ React.createElement(UserWishlistButton, {
        sku: sku,
        disabled: isAuthLoading || isUpdateLoading,
        isUpdateLoading: isUpdateLoading,
        onWishlistClick: onWishlistClick
    }) : /*#__PURE__*/ React.createElement(AnonymousWishlistButton, {
        sku: sku,
        disabled: isAuthLoading || isUpdateLoading,
        isUpdateLoading: isUpdateLoading,
        onWishlistClick: onWishlistClick,
        localShoppingListId: localShoppingList === null || localShoppingList === void 0 ? void 0 : localShoppingList.id
    });
};
const ConditionalButtonWrapper = (props)=>{
    const { isWishlistEnabled  } = useFeatureToggles();
    if (isWishlistEnabled) {
        return /*#__PURE__*/ React.createElement(WishlistButtonContainer, swcHelpers.extends({}, props));
    }
    return null;
};

export { ConditionalButtonWrapper as default };
//# sourceMappingURL=WishlistButtonContainer.js.map
